import { FieldValues, useForm } from 'react-hook-form';
import GrammerIcon from '@app/assets/icons/ai/grammar.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import LongerIcon from '@app/assets/icons/ai/text-longer.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import RestoreIcon from '@app/assets/icons/ai/restore.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import {
  fixGrammarAndSpelling,
  improveText,
  makeTextConcise,
  makeTextFormal,
  makeTextLonger,
  regenerateText,
} from '@app/services/service';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRequestById } from '@app/services/ratings';
import { createReview } from '@app/services/reviews';
import { getYelpCreateReviewUrl } from '@app/services/yelp';
import { getIntegration } from '@app/services/integrations';
import { IntegrationType, BAD_REVIEW_REASONS } from '@app/utils/consts';

export const useLeaveReview = () => {
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accessToken, setAccessToken] = useState<string | undefined>();

  const [originalReview, setOriginalReview] = useState<string>('');
  const [yelpUrl, setYelpUrl] = useState<string | null>(null);
  const [yelpLoading, setYelpLoading] = useState(false);
  const [googleUrl, setGoogleUrl] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const leaveReviewForm = useForm<{
    rating: number;
    review: string;
    reasons: string[];
    images: File[];
    aiSuggestion: boolean;
    contactEmail?: string;
    contactPhone?: string;
    visibility: 'private' | 'public';
  }>({
    defaultValues: {
      rating: 0,
      review: '',
      reasons: [],
      images: [],
      aiSuggestion: false,
      contactEmail: '',
      contactPhone: '',
      visibility: 'private'
    }
  });
  const params = useParams();
  const requestId = params.requestId ? params.requestId.split('&')[0] : '';

  const { data: requestData } = useQuery({
    queryKey: ['request-data'],
    queryFn: () => {
      return getRequestById(requestId);
    },
  });

  useEffect(() => {
    const checkYelpUrl = async () => {
      if (!requestId || !accessToken || !requestData?.data?.business?.id) return;
      setYelpLoading(true);
      try {
        const response = await getYelpCreateReviewUrl(accessToken, requestData.data.business.id);
        if (response.data?.url) {
          setYelpUrl(response.data.url);
        }
      } catch (e) {
        setYelpUrl(null);
      } finally {
        setYelpLoading(false);
      }
    };
    checkYelpUrl();
  }, [requestId, accessToken, requestData?.data?.business?.id]);

  useEffect(() => {
    const checkGoogleUrl = async () => {
      if (!requestId || !accessToken || !requestData?.data?.business?.id) return;
      setGoogleLoading(true);
      try {
        const response = await getIntegration(IntegrationType.GOOGLE, accessToken, requestData.data.business.id);
        if (response.data?.integration?.connectionId) {
          setGoogleUrl(response.data.integration.connectionId);
        }
      } catch (e) {
        setGoogleUrl(null);
      } finally {
        setGoogleLoading(false);
      }
    };
    checkGoogleUrl();
  }, [requestId, accessToken, requestData?.data?.business?.id]);

  const handleImageThumbnail = (file: File) => {
    const newThumbnail = URL.createObjectURL(file);
    setUploadedImages((prev) => [...prev, file]);
    setThumbnails((prev) => [...prev, newThumbnail]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || !selectedFiles?.length) {
      return;
    }

    if (selectedFiles) {
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
      ];
      const invalidFiles = Array.from(selectedFiles).filter(
        (file) => !allowedTypes.includes(file.type)
      );
      if (invalidFiles.length > 0) {
        e.target.value = '';
        console.error(
          'Invalid file format. Only JPEG, PNG, GIF, and WebP images are allowed.'
        );
        return;
      }
    }

    Array.from(selectedFiles).forEach((file) => {
      handleImageThumbnail(file);
    });
    // Reset the input value to allow selecting the same file again
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setUploadedImages((prev) => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });

    setThumbnails((prev) => {
      const newThumbnails = [...prev];
      URL.revokeObjectURL(newThumbnails[index]);
      newThumbnails.splice(index, 1);
      return newThumbnails;
    });
  };

  const handleStarClick = (star: number) => {
    leaveReviewForm.setValue('rating', star + 1);
  };

  const ratingValue = leaveReviewForm.watch('rating') ?? 0;
  const reviewValue = leaveReviewForm.watch('review') ?? '';

  const actionsFull = useMemo(
    () => [
      {
        label: 'Fix grammar & spelling',
        Icon: <GrammerIcon />,
        onClick: () => {
          fixGrammarAndSpelling(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
      {
        label: 'Make it concise',
        Icon: <ConciseIcon />,
        onClick: () => {
          makeTextConcise(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
      {
        label: 'Make it longer',
        Icon: <LongerIcon />,
        onClick: () => {
          makeTextLonger(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
      {
        label: 'Structure Help',
        Icon: <RestoreIcon />,
        onClick: () => {
          improveText(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
      {
        label: 'Formalize',
        Icon: <FormatIcon />,
        onClick: () => {
          makeTextFormal(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
      {
        label: 'Regenerate',
        Icon: <RegenerateIcon />,
        onClick: () => {
          regenerateText(reviewValue, accessToken).then((response) => {
            setOriginalReview(leaveReviewForm.getValues('review'));
            leaveReviewForm.setValue('review', response?.data?.text);
            leaveReviewForm.setValue('aiSuggestion', true);
          });
        },
      },
    ],
    [reviewValue, leaveReviewForm, accessToken]
  );

  const sanitizeFileNames = (files: File[]) => {
    return files.map((file) => {
      const nameWithoutExtension = file.name.substring(
        0,
        file.name.lastIndexOf('.')
      );
      const extension = file.name.substring(file.name.lastIndexOf('.'));
      const truncatedName = nameWithoutExtension.substring(0, 10);
      const sanitizedName = `${truncatedName.replace(/[^a-zA-Z0-9_.-]/g, '_')}${extension}`;
      return new File([file], sanitizedName, { type: file.type });
    });
  };

  const handleSubmit = (values: FieldValues, isFeedback: boolean = false) => {
    if (!values.rating) {
      return;
    }

    // Map selected reason values to their labels (only for ratings 1 or 2)
    // This handles both default bad reasons and custom feedback design tags    
    const selectedBadReasons = (values.rating < 3 && values.reasons && Array.isArray(values.reasons)) ? 
      values.reasons.map((reasonValue: string) => {
        // First try to find in default bad reasons
        const reasonObj = BAD_REVIEW_REASONS.find(reason => reason.value === reasonValue);
        if (reasonObj) {
          return reasonObj.label;
        }
        // If not found in default reasons, it's a custom tag from feedback design
        return reasonValue;
      }) : [];
    
    // Use the passed isFeedback parameter directly
    // If not explicitly set, fall back to the old logic for backward compatibility
    const finalIsFeedback = isFeedback !== undefined ? isFeedback : (values.rating < 3 && selectedBadReasons.length > 0);

    setIsSubmitting(true);
    createReview({
      rating: String(values.rating),
      comment: values.review,
      images: sanitizeFileNames(uploadedImages),
      requestId,
      accessToken,
      businessId: requestData?.data.businessId,
      badReasons: selectedBadReasons,
      isFeedback: finalIsFeedback,
    })
      .then((res) => {
        // Store review data in localStorage for thank you pages
        const reviewData = {
          review: values.review,
          rating: values.rating,
          yelpUrl: yelpUrl,
          googleUrl: googleUrl,
                  businessName: requestData?.data?.businessName,
        businessAddress: requestData?.data?.businessAddress,
          thumbnails: thumbnails,
          timestamp: new Date().toISOString(),
          usedPlatform: null // No platform used when submitting normally
        };
        localStorage.setItem('reviewData', JSON.stringify(reviewData));
        
        if (res?.data?.rating >= 3) {
          navigate(`/leave-review/${res.data.campaignId}/thank-you?t=${accessToken}`);
        } else {
          navigate(`/leave-review/${res.data.campaignId}/need-improvement?t=${accessToken}`);
        }
        leaveReviewForm.reset();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleBadReason = (reason: string) => {
    const currentReasons = leaveReviewForm.watch('reasons') ?? [];
    
    if (currentReasons.includes(reason)) {
      const newReasons = currentReasons.filter((r: string) => r !== reason);
      leaveReviewForm.setValue('reasons', newReasons);
    } else {
      const newReasons = [...currentReasons, reason];
      leaveReviewForm.setValue('reasons', newReasons);
    }
  
  };

  const handleDeclineAiSuggestion = () => {
    leaveReviewForm.setValue('review', originalReview);
    leaveReviewForm.setValue('aiSuggestion', false);
  };

  return {
    leaveReviewForm,
    handleStarClick,
    ratingValue,
    reviewValue,
    actionsFull,
    handleChange,
    thumbnails,
    setThumbnails,
    removeFile,
    handleSubmit,
    handleBadReason,
    requestData,
    originalReview,
    handleDeclineAiSuggestion,
    isSubmitting,
    accessToken,
    setAccessToken,
    yelpUrl,
    yelpLoading,
    googleUrl,
    googleLoading,
  };
};
